/**
 * The Substrate card: the material and the plate design laid out on it.
 *
 * The plate itself is the material (its length along the laser x axis, its
 * width along y); the fields here are the patch-group geometry of the plate
 * design config: patch size, the patch grid and its padding, the distance
 * between patch groups, the free margin along the plate edges and the
 * identifier label. The header is chosen on the Substrate position card. The
 * plate design's remaining keys (none of them read by the marking code)
 * stay as the template has them.
 */
import React from "react";
import { Stack, Typography } from "@mui/material";
import ChoiceCard from "./ChoiceCard";
import SettingFields, { getAt } from "./SettingFields";

export const PLATE_DESIGN_FIELDS = [
  { path: ["patch_size"], label: "Patch size", exclusiveMin: 0, unit: "mm", help: "Side of one colour patch" },
  { path: ["patch_group_grid_size", 0], label: "Patch rows", kind: "integer", min: 1, help: "Rows per patch group" },
  { path: ["patch_group_grid_size", 1], label: "Patch columns", kind: "integer", min: 1, help: "Columns per patch group" },
  { path: ["patch_group_grid_padding", 0], label: "Padding rows", kind: "integer", min: 0, help: "Empty rows inside the cross frame" },
  { path: ["patch_group_grid_padding", 1], label: "Padding columns", kind: "integer", min: 0, help: "Empty columns inside the cross frame" },
  { path: ["patch_group_distance"], label: "Group distance", min: 0, unit: "mm", help: "Gap between patch groups" },
  { path: ["plate_margin"], label: "Plate margin", min: 0, unit: "mm", help: "Kept free along every plate edge" },
  {
    path: ["identifier_label", "text_height"],
    label: "Label text height",
    min: 0,
    unit: "mm",
    optional: true,
    help: "Identifier under each patch group",
  },
  { path: ["identifier_label", "clearance"], label: "Label clearance", min: 0, unit: "mm", optional: true },
];

export function plateDesignFields(config) {
  return PLATE_DESIGN_FIELDS.filter((field) => !field.optional || getAt(config, field.path) !== undefined);
}

/** "7 × 7 patches of 1 mm". */
export function plateDesignSummary(config) {
  const grid = config?.patch_group_grid_size;
  if (!Array.isArray(grid) || config?.patch_size === undefined) return "";
  return `${grid[0]} × ${grid[1]} patches of ${config.patch_size} mm`;
}

export default function SubstrateCard({ rows, valueId, onMaterialChange, config, onConfigChange, onInvalidChange, substrate }) {
  const loaded = config && config.patch_size !== undefined;
  return (
    <Stack spacing={2.5} sx={{ width: "100%" }}>
      <ChoiceCard
        inputId="exploration-substrate"
        label="Substrate name"
        rows={rows}
        valueId={valueId}
        onChange={onMaterialChange}
        manage={{ text: "New substrates are created and managed on the", linkLabel: "Substrates", route: "/substrates" }}
      />
      {substrate ? (
        <Typography variant="body2" color="text.secondary" data-testid="substrate-plate">
          {`Plate ${substrate.length} × ${substrate.width} mm, ${substrate.thickness} mm thick (${substrate.material_type || "material"})`}
        </Typography>
      ) : null}
      {loaded ? (
        <Stack spacing={1} sx={{ width: "100%" }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Plate design
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Patch groups are laid out on the plate inside the marking field; the Substrate position card shows the
            resulting slots.
          </Typography>
          <SettingFields
            config={config}
            fields={plateDesignFields(config)}
            onChange={onConfigChange}
            onInvalidChange={onInvalidChange}
            testPrefix="plate"
          />
        </Stack>
      ) : null}
    </Stack>
  );
}
